<template>
  <div class="cx-dh-legend-configurator" id="cx-dh-legend-configurator">
    <div class="title-wrapper">
      <span class="legend-title"> Height difference legend configuration</span>
      <cx-button
        class="apply-legend"
        variantPrimary
        :disabled="!isFormValidated"
        @click="saveLegend">
        <span>Apply legend</span>
      </cx-button>
    </div>
    <cx-button
      class="add-range"
      variantPrimary
      flat
      icon="add"
      :disabled="isRangeAddDisabled"
      @click="addTopRange">
      <span>Add range</span>
    </cx-button>
    <div v-for="(item,index) in legend" :key="index" class="range">
      <cx-button
        class="remove-range"
        icon="close"
        flat
        :disabled="isRangeRemoveDisabled"
        @click="removeRange(index)"/>
      <cx-input
        v-if="index !== legend.length - 1"
        :validationRules="rangesValidation[index]"
        immediatelyShowErrors
        v-model="item.value"
        type="number" :step="0.1"
        class="input-range"/>
      <cx-input
        v-else
        disabled
        modelValue="<"
        type="text"
        class="input-range"/>
      <span>-</span>
      <cx-input
        disabled
        :modelValue="getStartValuesArray[index].value"
        type="text" :step="0.1"
        class="input-range"/>
      <input class="input-color" type="color" v-model="item.color"/>
    </div>
    <cx-button
      class="add-range"
      variantPrimary
      flat
      icon="add"
      :disabled="isRangeAddDisabled"
      @click="addBottomRange">
      <span>Add range</span>
    </cx-button>
  </div>
</template>

<script setup lang="ts">
import { ref, Ref, onMounted, watch, computed, ComputedRef } from 'vue';
import { useStore } from 'vuex';
import { Surfacelog, SurfacelogDeltaHeightLegend } from '@/domain/surfacelog/Surfacelog';
import CxButton from '@/components/CxButton.vue';
import CxInput from '@/components/CxInput.vue';
import validatonRules from '@/components/validationRules';
import { ValidationRuleWithParams } from '@vuelidate/core';

const { required, cxFloat, cxGreaterThan, cxLessThan } = validatonRules();

const store = useStore();

const legend: Ref<SurfacelogDeltaHeightLegend> = ref([]);
const rangesValidation: Ref<any> = ref(undefined);
const isFormValidated: Ref<boolean> = ref(true);
const maxRanges: Ref<number> = ref(9);
const minRanges: Ref<number> = ref(3);

const selectedEntity: ComputedRef<Surfacelog> = computed(() => store.getters['map/selectedEntity']);
const getDeltaHeightLegend: Ref<(layerUuid: string | null) => SurfacelogDeltaHeightLegend> = computed(() => (layerUuid: string | null) => store.getters['detailsSurfacelog/getDeltaHeightLegend'](layerUuid));

const changeSurfacelogLegend = (newLegend: any) => { store.dispatch('detailsSurfacelog/changeSurfacelogLegend', newLegend); };

const selectedEntityUuid = computed(() => {
  if (selectedEntity.value) {
    return selectedEntity.value.uuid;
  }
  return null;
});

interface ValidationRules {
  modelValue: {
    required: ValidationRuleWithParams
    cxFloat: ValidationRuleWithParams
    $autoDirty: boolean
    greaterThan?: ValidationRuleWithParams
    lessThan?: ValidationRuleWithParams
  }
}

const getRangesValidation = computed(() => {
  return legend.value.map((el, i) => {
    const rules: ValidationRules = {
      modelValue: {
        required,
        cxFloat,
        $autoDirty: true
      }
    };
    if (i === 0) {
      if (!Number.isNaN(parseFloat(String(legend.value[i + 1].value)))) {
        rules.modelValue.greaterThan = cxGreaterThan(legend.value[i + 1].value);
      }
      return rules;
    }
    if (i === legend.value.length - 2) {
      if (!Number.isNaN(parseFloat(String(legend.value[i - 1].value)))) {
        rules.modelValue.lessThan = cxLessThan(legend.value[i - 1].value);
      }
      return rules;
    }
    if (i === legend.value.length - 1) {
      return null;
    }
    if (!Number.isNaN(parseFloat(String(legend.value[i + 1].value)))) {
      rules.modelValue.greaterThan = cxGreaterThan(legend.value[i + 1].value);
    }
    if (!Number.isNaN(parseFloat(String(legend.value[i - 1].value)))) {
      rules.modelValue.lessThan = cxLessThan(legend.value[i - 1].value);
    }
    return rules;
  });
});

const getStartValuesArray = computed(() => {
  return legend.value.map((r, i) => {
    if (i === 0) return { value: '<' };
    return legend.value[i - 1];
  });
});

const saveLegend = () => {
  selectedEntity.value.deltaHeightLegend = legend.value;
  legend.value[legend.value.length - 1].value = legend.value[legend.value.length - 2].value - 0.01;
  changeSurfacelogLegend({ layerUuid: selectedEntityUuid.value, legend: legend.value, isOutsideRefModelVisible: selectedEntity.value.isOutsideRefModel });
};

const isRangeAddDisabled = computed(() => {
  return legend.value.length === maxRanges.value;
});

const isRangeRemoveDisabled = computed(() => {
  return legend.value.length === minRanges.value;
});

const addTopRange = () => {
  legend.value.unshift({ value: parseFloat(String(legend.value[0].value)) + 0.01, color: '#000000' });
};

const addBottomRange = () => {
  legend.value.push({ value: parseFloat(String(legend.value[legend.value.length - 1].value)) - 0.01, color: '#000000' });
};

const removeRange = (index) => {
  if (legend.value.length > minRanges.value) {
    legend.value.splice(index, 1);
  }
};

watch(selectedEntityUuid, () => {
  legend.value = getDeltaHeightLegend.value(selectedEntityUuid.value);
});

watch(
  () => legend.value,
  async () => {
    rangesValidation.value = getRangesValidation.value;
    requestAnimationFrame(() => {
      isFormValidated.value = !(document.querySelectorAll('.cx-dh-legend-configurator .cx-input-validation').length);
    });
  },
  { deep: true }
);

onMounted(async () => {
  legend.value = getDeltaHeightLegend.value(selectedEntityUuid.value);
  rangesValidation.value = getRangesValidation.value;
});
</script>

<style lang="less">
@import '../../../common';

.cx-dh-legend-configurator {
  color: @textColor;
  flex: 0 1 auto;
  margin-left: auto;
  margin-top: 31px;
  width: auto;
  max-width: @detailsMPointMetadataPanelWidth;
  max-height: @detailsMPointMetadataPanelHeight;

  .title-wrapper {
    display: flex;
    align-items: center;
    gap: 10px;

    .legend-title {
      font-size: 12px;
    }

    .apply-legend {
      padding: 0px 10px 0px 10px;
    }
  }

  .range {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 9px;
    margin-bottom: 2px;
  }
  input[type="color"]::-webkit-color-swatch-wrapper {
    padding: 0 2px;
  };

  .input-color {
    -webkit-appearance: none;
    -moz-appearance: none;
    appearance: none;
    font-size: @appFontSize;
    width: 36px;
    height: 14px;
    background-color: transparent;
    border: 2px;
    cursor: pointer;
    box-sizing: border-box;
    flex: 0 1 36px;
  }
  &.nolicence {
    max-height: @detailsMPointMetadataPanelHeighNoLicence;
  }
  .flex(column);

  .input-range, .input-range input {
    max-width: 80px;
  }
  .cx-input .cx-input-container {
    display: -webkit-box;
    display: -moz-box;

    .cx-input-wrapper {
      padding: 0px 6px 0px 10px;
      display: flex;
      align-items: center;
    }
  }

  .cx-input .cx-input-validation {
    color: @highlightRedColor;
    text-align: right;
    top: 0;
    left: -180px;
    width: 150px;
    line-height: 1;
  }

  .add-range {
    padding: 4px 0;
  }

  .remove-range {
    flex: 0 1 24px;
  }
}
</style>